import React, { useState, useEffect } from 'react';
import { useTranslate } from '../../Hook/useTranslate';
import { getCatalog } from '../../services/catalogAPI';
import './RestaurantFiltersModal.css';

// Everything the filters can hold, cleared. Shared with the home page so every "clear" resets the same set.
export const DEFAULT_RESTAURANT_FILTERS = {
  sortBy: 'Relevance',
  fastDelivery: false,
  rating: null,
  offers: false,
  costRange: [],
  foodType: [],
  pureVeg: false,
  openNow: false,
  newRestaurants: false,
  cuisines: [],
  groupTags: [],
  maxDistanceKm: null,
};

// Same distance steps as the app's filter drawer.
export const DISTANCE_OPTIONS_KM = [1, 3, 5, 10];

const RestaurantFiltersModal = ({ isOpen, onClose, initialFilters, onApply }) => {
  const [filters, setFilters] = useState(initialFilters);
  const [catalog, setCatalog] = useState({ cuisines: [], groups: [] });
  const t = useTranslate();

  useEffect(() => {
    if (isOpen) {
      setFilters({
        cuisines: [],
        groupTags: [],
        ...initialFilters
      });

      // Cuisine and dish-tag filters come from the admin catalog.
      getCatalog().then(setCatalog).catch(() => setCatalog({ cuisines: [], groups: [] }));
    }
  }, [isOpen, initialFilters]);

  if (!isOpen) return null;

  const handleSortChange = (value) => {
    setFilters({ ...filters, sortBy: value });
  };

  const handleCheckboxChange = (field) => {
    setFilters({ ...filters, [field]: !filters[field] });
  };

  const handleRadioChange = (field, value) => {
    setFilters({ ...filters, [field]: filters[field] === value ? null : value });
  };

  const handleArrayChange = (field, value) => {
    const current = filters[field] || [];
    if (current.includes(value)) {
      setFilters({ ...filters, [field]: current.filter(item => item !== value) });
    } else {
      setFilters({ ...filters, [field]: [...current, value] });
    }
  };

  const handleClearAll = () => {
    setFilters({ ...DEFAULT_RESTAURANT_FILTERS });
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  return (
    <div className="filter-modal-overlay" onClick={onClose}>
      <div className="filter-modal-content" onClick={e => e.stopPropagation()}>
        <div className="filter-modal-header">
          <h2>{t("Filters & Sorting")}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="filter-modal-body">
          {/* Sort By */}
          <div className="filter-section">
            <h3>{t("sortBy")}</h3>
            <div className="filter-options-grid">
              {['Relevance', 'Delivery Time', 'Rating', 'Cost Low to High', 'Cost High to Low'].map(option => (
                <button
                  key={option}
                  className={`filter-chip ${filters.sortBy === option ? 'selected' : ''}`}
                  onClick={() => handleSortChange(option)}
                >
                  {t(option)}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-divider"></div>

          {/* Quick Filters */}
          <div className="filter-section">
            <h3>{t("quickFilters")}</h3>
            <div className="filter-options-grid">
              <button
                className={`filter-chip ${filters.fastDelivery ? 'selected' : ''}`}
                onClick={() => handleCheckboxChange('fastDelivery')}
              >
                {t("fastDeliveryLimit")}
              </button>
              <button
                className={`filter-chip ${filters.offers ? 'selected' : ''}`}
                onClick={() => handleCheckboxChange('offers')}
              >
                {t("offersBogoFree")}
              </button>
              <button
                className={`filter-chip ${filters.pureVeg ? 'selected' : ''}`}
                onClick={() => handleCheckboxChange('pureVeg')}
              >
                {t("Pure Veg")}
              </button>
              <button
                className={`filter-chip ${filters.openNow ? 'selected' : ''}`}
                onClick={() => handleCheckboxChange('openNow')}
              >
                {t("Open Now")}
              </button>
              <button
                className={`filter-chip ${filters.newRestaurants ? 'selected' : ''}`}
                onClick={() => handleCheckboxChange('newRestaurants')}
              >
                {t("newRestaurants")}
              </button>
            </div>
          </div>

          <div className="filter-divider"></div>

          {/* Cuisines (admin catalog) */}
          {catalog.cuisines.length > 0 && (
            <>
              <div className="filter-section">
                <h3>{t("Cuisines")}</h3>
                <div className="filter-options-grid">
                  {catalog.cuisines.map(c => (
                    <button
                      key={c._id}
                      className={`filter-chip ${(filters.cuisines || []).includes(c.name) ? 'selected' : ''}`}
                      onClick={() => handleArrayChange('cuisines', c.name)}
                    >
                      {t(c.name)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="filter-divider"></div>
            </>
          )}

          {/* Dish tags, one section per admin group */}
          {catalog.groups.map(group => (
            <React.Fragment key={group._id}>
              <div className="filter-section">
                <h3>{t(group.name)}</h3>
                <div className="filter-options-grid">
                  {group.tags.map(tag => (
                    <button
                      key={tag._id}
                      className={`filter-chip ${(filters.groupTags || []).includes(tag._id) ? 'selected' : ''}`}
                      onClick={() => handleArrayChange('groupTags', tag._id)}
                    >
                      {t(tag.name)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="filter-divider"></div>
            </React.Fragment>
          ))}

          {/* Distance from the delivery location */}
          <div className="filter-section">
            <h3>{t("Distance")}</h3>
            <div className="filter-options-grid">
              {DISTANCE_OPTIONS_KM.map(km => (
                <button
                  key={km}
                  className={`filter-chip ${filters.maxDistanceKm === km ? 'selected' : ''}`}
                  onClick={() => handleRadioChange('maxDistanceKm', km)}
                >
                  {t("Within")} {km} km
                </button>
              ))}
            </div>
          </div>

          <div className="filter-divider"></div>

          {/* Rating */}
          <div className="filter-section">
            <h3>{t("rating")}</h3>
            <div className="filter-options-grid">
              <button
                className={`filter-chip ${filters.rating === '4.5' ? 'selected' : ''}`}
                onClick={() => handleRadioChange('rating', '4.5')}
              >
                {t("4.5+")}
              </button>
              <button
                className={`filter-chip ${filters.rating === '4.0' ? 'selected' : ''}`}
                onClick={() => handleRadioChange('rating', '4.0')}
              >
                {t("4.0+")}
              </button>
            </div>
          </div>
        </div>

        <div className="filter-modal-footer">
          <button className="clear-all-btn" onClick={handleClearAll}>{t("clearAll")}</button>
          <button className="apply-btn" onClick={handleApply}>{t("applyFilters")}</button>
        </div>
      </div>
    </div>
  );
};

export default RestaurantFiltersModal;